import { useSyncExternalStore } from 'react';

/**
 * Hook for responsive design with media queries.
 */
export const useMediaQuery = (query: string): boolean => {
  const subscribe = (onStoreChange: () => void) => {
    if (typeof globalThis.window === 'undefined' || typeof globalThis.matchMedia !== 'function') {
      return () => {};
    }

    const mql: MediaQueryList = globalThis.matchMedia(query);
    const handleChange = () => onStoreChange();

    // Prefer modern event API, but fallback for older browsers
    if (
      typeof (mql as MediaQueryList & { addEventListener?: unknown }).addEventListener ===
      'function'
    ) {
      // addEventListener signature expects EventListener, safe cast here
      mql.addEventListener('change', handleChange as unknown as EventListener);
      return () => mql.removeEventListener('change', handleChange as unknown as EventListener);
    } else {
      // Deprecated older API support for Safari/old Chrome
      mql.addListener(handleChange);
      return () => mql.removeListener(handleChange);
    }
  };

  const getSnapshot = () => {
    if (typeof globalThis.window === 'undefined' || typeof globalThis.matchMedia !== 'function') {
      return false;
    }
    return globalThis.matchMedia(query).matches;
  };

  const getServerSnapshot = () => false;

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};

/**
 * Common responsive hooks.
 * Mobile: <= 768px (phones)
 * Tablet: 769px - 1023px
 * Desktop: >= 1024px
 */
export const useIsMobile = () => useMediaQuery('(max-width: 768px)');
export const useIsTablet = () => useMediaQuery('(min-width: 769px) and (max-width: 1023px)');
export const useIsDesktop = () => useMediaQuery('(min-width: 1024px)');
